import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  User,
} from "firebase/auth";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  runTransaction,
  setDoc,
  startAfter,
  serverTimestamp,
} from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { auth, db, storage, functions } from "../../shared/firebase";
import { useDarkMode } from "../../shared/theme";
import { COMERCIAL_PEOPLE } from "../../cases/cofidisAgentConfig";
import easyDebtLogo from "../../assets/easydebt-logo.svg";
import {
  cofidisCaseHasPhone,
  cofidisPhoneExactVariants,
  cofidisPhoneSearchTokens,
  phoneKey,
} from "../Andi/clientFormMatch";
import { canMarkWhatsappThreadRead, canReturnToComercial, OPERACIONES_PEOPLE } from "./whatsappPermissions";
import { pegadoAbajo } from "./scrollChat";
import { unreadThreadsForArea } from "./unreadThreads";
import { matchesAssignmentFilter, UNASSIGNED_FILTER } from "./assignmentFilter";
import { whatsappDeepLinkPhone } from "./deepLink";
import { crmLeadIdForWhatsappThread, operationsHandoffPatch } from "./operationsAssignment";
import { listenMessages } from "./mensajesChat";
import { escucharEspejo, leerEspejo, leerUsuarios, mensajeLocal, parcheLocal } from "../../shared/vpsMirror";
import { anterioresDeArea, buscarEnArea, chatPorId, noLeidos, recientesDeArea } from "./lecturasWhatsapp";
import { decideTemplateSending } from "./templateSendingPolicy";

// Assignment is split into two separate dropdowns (Andre's request): commercials
// and operaciones people.
const COMERCIALES = COMERCIAL_PEOPLE;
// OPERACIONES_PEOPLE vive en whatsappPermissions.ts: ver el comentario de alli.
const ASSIGNEES = Array.from(new Set([...COMERCIALES, ...OPERACIONES_PEOPLE]));

type Area = "comercial" | "operaciones";

interface Thread {
  id: string;
  name?: string;
  phone?: string;
  area?: Area;
  status?: string;
  lastMessageAt?: any;
  lastInboundAt?: any;
  lastTemplateAt?: any;
  lastMessagePreview?: string;
  caseId?: string;
  source?: string;
  assignedBy?: string;
  assignedAt?: any;
  createdAt?: any;
  lastMessageDirection?: "inbound" | "outbound" | "system";
  assignedComerciales?: string[];
  assignedComercial?: string;
  assignedOperaciones?: string;
  lastAgent?: string;
  hasUnread?: boolean;
}

interface Attachment {
  kind?: string;
  downloadUrl?: string;
  contentType?: string;
  name?: string;
}
interface Msg {
  id: string;
  direction?: "inbound" | "outbound" | "system";
  body?: string;
  event?: string;
  note?: string;
  templateName?: string;
  status?: string;
  deliveryStatus?: string;
  deliveryError?: { code?: number; title?: string; details?: string };
  attachments?: Attachment[];
  at?: any;
  by?: string;
}
interface Snippet {
  id: string;
  label?: string;
  text: string;
  order?: number;
  by?: string;
  area?: Area;
}

interface Template {
  id: string;
  name: string;
  language?: string;
  category?: string;
  status?: string;
  preview?: string;
  variableCount?: number;
}

type View = "chats" | "templates" | "snippets";

const AREA_LABEL: Record<Area, string> = {
  comercial: "Comercial",
  operaciones: "Operaciones",
};

// Lead pipeline. Comercial statuses first (default "nuevo"), then Operaciones (OP-*).
const STATUSES: { id: string; label: string; color: string }[] = [
  { id: "nuevo", label: "Nuevo", color: "bg-sky-500/15 text-sky-600" },
  { id: "en_comunicacion", label: "En comunicación", color: "bg-indigo-500/15 text-indigo-600" },
  { id: "no_contesta", label: "No contesta", color: "bg-amber-500/15 text-amber-600" },
  { id: "no_viable", label: "No viable", color: "bg-destructive/15 text-destructive" },
  { id: "no_viable_ratio", label: "No viable por ratio", color: "bg-destructive/15 text-destructive" },
  { id: "cerrado", label: "Cerrado", color: "bg-muted text-muted-foreground" },
  // Operaciones stages
  { id: "op_doc_solicitada", label: "OP - Doc Solicitada", color: "bg-cyan-500/15 text-cyan-600" },
  { id: "op_evaluacion", label: "OP - Evaluación", color: "bg-violet-500/15 text-violet-600" },
  { id: "op_aprobado", label: "OP - Aprobado", color: "bg-emerald-500/15 text-emerald-600" },
  { id: "op_rechazado", label: "OP - Rechazado", color: "bg-destructive/15 text-destructive" },
  { id: "op_desembolso", label: "OP - Desembolso", color: "bg-green-600/15 text-green-700" },
];
const statusOf = (id?: string) => STATUSES.find((s) => s.id === id) || STATUSES[0];

// Nombres reales del equipo (petición "Cambio de nombres"): usuario → nombre.
const DISPLAY_NAMES: Record<string, string> = {
  "andre.sandoval": "Andre Sandoval",
  "diego.catter": "Diego Catter",
  "alamo.madrid": "Alamo Cusihuaman",
  "francesco.larosa": "Francesco La Rosa",
  "sandy.hernandez": "Sandy Hernandez",
  "comercial.ap1": "Yeniffer Quevedo",
  "comercial.ap2": "Jonathan Alfonso",
  "zhongyun.he": "ZY",
  // Altas 25/08: equipo comercial nuevo + operaciones
  "jefferson.madrid": "Jefferson Merino",
  "jasmin.madrid": "Jasmin Suarez",
  "comercial.ap.tl": "Suyin Rodríguez",
  "comercial.ap3": "Fernando Rondón",
  "comercial.ap4": "Kimberlin Rincon",
  "comercial.ap5": "Carina Hernández",
  "comercial.ap6": "Andrea Sánchez",
  "comercial.ap7": "Cristian Vicent",
  "comercial.ap8": "Alba Pérez",
  "comercial.ap9": "Linda Montoya",
  "comercial.ap10": "Alexandra Gomez",
  "gabriela.lume": "Gabriela Lume",
  "joaquin.uribe": "Joaquin Uribe",
  "andrea.leyva": "Andrea Leyva",
  "genesis.paredes": "Génesis Paredes",
  "andrea.cornejo": "Andrea Cornejo",
  "alejandro.martinez": "Alejandro Martinez",
};
// e.g. "andre.sandoval@easydebt.es" → "Andre Sandoval"; unmapped users keep the
// username; "auto" stays "auto". Display-only — stored values keep the email.
const shortName = (v?: string) => {
  if (!v) return "";
  const s = String(v);
  const user = s.includes("@") ? s.split("@")[0] : s;
  return DISPLAY_NAMES[user.toLowerCase()] || user;
};

const DAY_MS = 24 * 60 * 60 * 1000;
const toMillis = (ts: any) => (ts?.toDate ? ts.toDate().getTime() : ts ? new Date(ts).getTime() : 0);

const normText = (v: string) =>
  String(v || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();

// Search index for a thread: prefixes of the phone (full + national) and name,
// so the whole inbox is searchable with a single cheap array-contains query.
const buildThreadPrefixes = (phone: string, name: string): string[] => {
  const set = new Set<string>();
  const d = String(phone || "").replace(/\D/g, "");
  const nat = d.startsWith("34") ? d.slice(2) : d;
  for (const num of [d, nat]) for (let l = 3; l <= Math.min(num.length, 10); l++) set.add("phone:" + num.slice(0, l));
  for (const w of normText(name).split(" ").filter(Boolean)) for (let l = 3; l <= Math.min(w.length, 10); l++) set.add("q:" + w.slice(0, l));
  return Array.from(set).sort();
};

// The single token to query for a given search input (phone if it's digits).
const searchToken = (input: string): string => {
  const digits = input.replace(/\D/g, "");
  if (digits.length >= 3) return "phone:" + digits.slice(0, 10);
  const w = normText(input).split(" ").filter(Boolean)[0] || "";
  return w.length >= 3 ? "q:" + w.slice(0, 10) : "";
};

const inputClass =
  "w-full rounded-lg border border-border bg-card px-4 py-3 text-sm text-foreground outline-none transition-colors focus:border-primary";
const btnPrimary =
  "rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40";
const ADMIN_APP_URL = "https://admin.easydebt.es";
// Escritura en un chat: con el espejo del VPS se ve al instante (el listener
// de Firestore también la enseñaba sin esperar) y se retira si Firestore la
// rechaza.
const actualizarChat = (id: string, cambios: Record<string, any>) => {
  const deshacer = parcheLocal("whatsappThreads", id, cambios);
  return updateDoc(doc(db, "whatsappThreads", id), cambios).catch((error) => {
    deshacer();
    throw error;
  });
};

// Mensaje nuevo: con el espejo se ve al momento, sin esperar a Firestore ni
// al VPS, y se retira si Firestore lo rechaza. El id se elige aquí (como hace
// addDoc) para poder enseñarlo antes; setDoc sobre un id nuevo es un `create`.
const nuevoMensaje = async (threadId: string, datos: Record<string, any>) => {
  const ref = doc(collection(db, "whatsappThreads", threadId, "messages"));
  const retirar = mensajeLocal(threadId, ref.id, datos);
  try {
    await setDoc(ref, datos);
  } catch (error) {
    retirar();
    throw error;
  }
  return ref;
};

const clientFormUrl = (caseId: string) =>
  `${ADMIN_APP_URL}/#/cofidisCases/${encodeURIComponent(caseId)}/show`;

// Uno para toda la aplicacion, no uno por burbuja de mensaje ni por fila de
// la lista de chats: toLocaleString construye un Intl.DateTimeFormat cada vez.
const FMT_HORA = new Intl.DateTimeFormat("es-ES", {
  day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
});
const fmtTime = (ts: any) => {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return isNaN(d.getTime()) ? "" : FMT_HORA.format(d);
};

const WhatsAppApp = () => {
  const [checking, setChecking] = useState(true);
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [waGroups, setWaGroups] = useState<string[]>([]);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPass, setLoginPass] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const [dark, toggleDark] = useDarkMode();
  const [view, setView] = useState<View>("chats");
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);
  const [tab, setTab] = useState<Area>("comercial");
  const [unreadByArea, setUnreadByArea] = useState<{ comercial: number; operaciones: number }>({ comercial: 0, operaciones: 0 });
  const [unreadThreads, setUnreadThreads] = useState<Thread[]>([]);
  const [threads, setThreads] = useState<Thread[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [moreMessages, setMoreMessages] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState(false);
  const [legacyInbound, setLegacyInbound] = useState<{ id: string; at: number } | null>(null);
  const messagePages = useRef<ReturnType<typeof listenMessages> | null>(null);
  const historyScroll = useRef<{ height: number; top: number } | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [snippets, setSnippets] = useState<Snippet[]>([]);
  const [templateName, setTemplateName] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [actionErr, setActionErr] = useState("");
  const [showHandoff, setShowHandoff] = useState(false);
  const [handoffNote, setHandoffNote] = useState("");
  const [threadSearch, setThreadSearch] = useState("");   // #10 search chat list
  const [chatSearch, setChatSearch] = useState("");        // #10 search within chat
  const [showChatSearch, setShowChatSearch] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);     // #4/#8 no-leídos filter
  const [comercialFilter, setComercialFilter] = useState(""); // filter by assigned agent
  const [searchResults, setSearchResults] = useState<Thread[]>([]);
  const [searching, setSearching] = useState(false);
  const [recording, setRecording] = useState(false);       // voice-note recording
  const [pendingAudio, setPendingAudio] = useState<{ blob: Blob; url: string; type: string } | null>(null);
  const mediaRecorderRef = useRef<any>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const bottomRef = useRef<HTMLDivElement | null>(null);   // auto-scroll anchor
  const listaMsgRef = useRef<HTMLDivElement | null>(null);
  // Si el que mira estaba pegado al final o se habia ido a releer mas arriba.
  const pegadoRef = useRef(true);
  const composerRef = useRef<HTMLTextAreaElement | null>(null); // para devolverlo a una línea al enviar
  const prevUnreadRef = useRef<number | null>(null);        // to detect new msgs
  const audioCtxRef = useRef<any>(null);
  const [showNewChat, setShowNewChat] = useState(false);   // #1 start new chat
  const [newChatPhone, setNewChatPhone] = useState("");
  const [editingName, setEditingName] = useState(false);   // #9 rename client
  const [nameDraft, setNameDraft] = useState("");
  const [now, setNow] = useState(() => Date.now());

  // ── Auth + profile ───────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setAuthUser(u);
      if (u) {
        try {
          // Del VPS (en memoria); si no contesta, de Firestore.
          const data: any = await leerUsuarios().then((filas) => filas.find((f) => f.id === u.uid) || {})
            .catch(() => getDoc(doc(db, "users", u.uid)).then((snap) => (snap.exists() ? snap.data() : {})));
          setRole((data.role as string) || null);
          setWaGroups(Array.isArray(data.waGroups) ? data.waGroups : []);
        } catch {
          setRole(null);
          setWaGroups([]);
        }
      } else {
        setRole(null);
        setWaGroups([]);
      }
      setChecking(false);
    });
    return unsub;
  }, []);

  // WhatsApp roles come from waGroups ONLY, not the CRM global role: most staff
  // are role="admin" for the admin panel, but WhatsApp admin must stay limited
  // to the members of the "admin" waGroup (Andre, Diego, ZY).
  const isAdmin = waGroups.includes("admin");
  const canComercial = isAdmin || waGroups.includes("comercial");
  const canOperaciones = isAdmin || waGroups.includes("operaciones");
  const canReturnToClients = canReturnToComercial(waGroups);
  const allowed = !!authUser && waGroups.length > 0;

  // ANDI links here with ?phone=<international digits>. Open the matching
  // Cloud API conversation directly; if it does not exist yet, open the safe
  // creation dialog prefilled (nothing is created or sent automatically).
  const deepLinkHandledRef = useRef(false);
  useEffect(() => {
    if (!allowed || deepLinkHandledRef.current) return;
    const phone = whatsappDeepLinkPhone(window.location.search);
    if (!phone) return;

    let cancelled = false;
    const openLinkedChat = async () => {
      try {
        // Los chats ya están en esta pestaña (espejo); Firestore solo si el VPS
        // no sirve. Si el chat se acaba de crear y el espejo aún no lo tiene,
        // se abre el diálogo de chat nuevo, y crearlo vuelve a mirar Firestore.
        const filas = await leerEspejo("whatsappThreads", (todas) => todas.filter((t) => t.phone === phone).slice(0, 1))
          .catch(async () => {
            const snap = await getDocs(query(
              collection(db, "whatsappThreads"),
              where("phone", "==", phone),
              limit(1)
            ));
            return snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
          });
        if (cancelled) return;
        deepLinkHandledRef.current = true;
        setView("chats");
        setUnreadOnly(false);
        setComercialFilter("");
        setThreadSearch("");
        if (!filas.length) {
          setNewChatPhone(phone);
          setShowNewChat(true);
          return;
        }
        const linked = filas[0];
        const linkedArea = ((linked as any).area || "comercial") as Area;
        const canOpen = linkedArea === "operaciones" ? canOperaciones : canComercial;
        if (!canOpen) {
          setThreadSearch(phone);
          return;
        }
        setTab(linkedArea);
        setSelectedId(linked.id);
      } catch {
        if (!cancelled) {
          deepLinkHandledRef.current = true;
          setThreadSearch(phone);
        }
      }
    };
    openLinkedChat();
    return () => { cancelled = true; };
  }, [allowed, canComercial, canOperaciones]);

  useEffect(() => {
    // pick a default tab the user can see
    if (!canComercial && canOperaciones) setTab("operaciones");
  }, [canComercial, canOperaciones]);

  // ── Threads for the active area (real-time) ──────────────────────────
  // Cost control: only the 150 most-recent chats are shown live (not the whole
  // collection). Older chats are reachable via search; see #10.
  // Los chats se leen del espejo del VPS (un solo listener para todo el
  // equipo); si no responde, del listener de Firestore de siempre.
  useEffect(() => {
    if (!allowed) return;
    return escucharEspejo("whatsappThreads", recientesDeArea(tab), (filas) => setThreads(filas as Thread[]), () => {
      const q = query(
        collection(db, "whatsappThreads"),
        where("area", "==", tab),
        orderBy("lastMessageAt", "desc"),
        limit(150)
      );
      return onSnapshot(
        q,
        (snap) => setThreads(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))),
        () => setThreads([])
      );
    });
  }, [allowed, tab]);

  // Global unread counters per area (bell / tab badges). A chat is "unread" when
  // a new inbound message arrived and nobody has OPENED it yet (cleared on open,
  // WhatsApp-style — not only on reply). Cost control: query only unread threads.
  useEffect(() => {
    if (!allowed) return;
    const recibir = (allUnread: Thread[]) => {
      let c = 0;
      let o = 0;
      allUnread.forEach((t) => {
        if ((t.area || "comercial") === "operaciones") o += 1;
        else c += 1;
      });
      setUnreadThreads(allUnread);
      setUnreadByArea({ comercial: c, operaciones: o });
    };
    return escucharEspejo("whatsappThreads", noLeidos, (filas) => recibir(filas as Thread[]), () => onSnapshot(
      query(collection(db, "whatsappThreads"), where("hasUnread", "==", true)),
      (snap) => recibir(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as Thread[]),
      () => {
        setUnreadThreads([]);
        setUnreadByArea({ comercial: 0, operaciones: 0 });
      }
    ));
  }, [allowed]);

  // #3 Al abrir un chat, siempre al ultimo mensaje.
  useEffect(() => {
    pegadoRef.current = true;
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [selectedId]);

  // Mensajes nuevos: solo se baja si ya estabas abajo. El listener devuelve un
  // array nuevo en cada snapshot aunque no haya cambiado nada, asi que antes
  // esto te devolvia al final mientras releias algo mas arriba.
  useEffect(() => {
    if (pegadoRef.current) bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages]);
  useLayoutEffect(() => {
    const before = historyScroll.current;
    const el = listaMsgRef.current;
    if (before && el && !loadingMessages) {
      el.scrollTop = before.top + el.scrollHeight - before.height;
      historyScroll.current = null;
    }
  }, [messages, loadingMessages]);

  const loadOlderMessages = () => {
    if (loadingMessages || !moreMessages) return;
    const el = listaMsgRef.current;
    if (el) historyScroll.current = { height: el.scrollHeight, top: el.scrollTop };
    pegadoRef.current = false;
    messagePages.current?.more();
  };

  // #4 Alert (short beep) + tab-title badge when new messages arrive.
  const beep = () => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.36);
    } catch {
      /* audio not available */
    }
  };
  useEffect(() => {
    const total = (canComercial ? unreadByArea.comercial : 0) + (canOperaciones ? unreadByArea.operaciones : 0);
    document.title = total > 0 ? `(${total}) WhatsApp · EasyDebt` : "WhatsApp · EasyDebt";
    if (prevUnreadRef.current === null) {
      prevUnreadRef.current = total; // skip the initial load
      return;
    }
    if (total > prevUnreadRef.current) beep(); // a new unread appeared
    prevUnreadRef.current = total;
  }, [unreadByArea, canComercial, canOperaciones]);

  // Solo se transmiten los 150 chats más recientes, así que un chat viejo
  // encontrado con el buscador no estaba en esa lista y al pulsarlo la
  // conversación salía vacía (André, 28/08). Si no está entre los 150, se
  // escucha ese chat suelto por su id, y así también sigue abierto cuando se
  // borra la búsqueda.
  const inList = threads.some((t) => t.id === selectedId);
  const [extraThread, setExtraThread] = useState<Thread | null>(null);
  useEffect(() => {
    if (!selectedId || inList) { setExtraThread(null); return; }
    return escucharEspejo("whatsappThreads", chatPorId(selectedId),
      (filas) => setExtraThread((filas[0] as Thread) || null),
      () => onSnapshot(
        doc(db, "whatsappThreads", selectedId),
        (d) => setExtraThread(d.exists() ? ({ id: d.id, ...(d.data() as any) }) : null),
        () => setExtraThread(null)
      ));
  }, [selectedId, inList]);

  // Cerrar el chat solo si es de la otra bandeja. Antes se cerraba cualquiera
  // que no estuviese entre los 150 recientes, y por eso los chats viejos que
  // salían en el buscador se quedaban en blanco al pulsarlos (André, 28/08).
  useEffect(() => {
    if (!selectedId || inList) return;
    if (extraThread && extraThread.id === selectedId && (extraThread.area || "comercial") !== tab) {
      setSelectedId(null);
      setMessages([]);
    }
  }, [threads, selectedId, inList, extraThread, tab]);

  // ── Messages of the selected thread (real-time) ──────────────────────
  useEffect(() => {
    setMessages([]);
    setMoreMessages(false);
    setLoadingMessages(!!selectedId);
    setMessagesError(false);
    historyScroll.current = null;
    if (!selectedId) {
      return;
    }
    const pages = listenMessages(db, selectedId, (state) => {
      setMessages(state.messages);
      setMoreMessages(state.more);
      setLoadingMessages(state.loading);
      setMessagesError(state.error);
    });
    messagePages.current = pages;
    return () => { pages.close(); messagePages.current = null; };
  }, [selectedId]);

  // Searching remains a search of the conversation, including older history.
  // Load it progressively only when requested; never report no matches while
  // earlier pages still remain unread.
  useEffect(() => {
    if (chatSearch.trim() && moreMessages && !loadingMessages && !messagesError) messagePages.current?.more();
  }, [chatSearch, moreMessages, loadingMessages, messagesError, messages]);

  // ── Templates (real-time so the management page reflects a sync) ──────
  useEffect(() => {
    if (!allowed) return;
    const unsub = onSnapshot(
      collection(db, "whatsappTemplates"),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setTemplates(list);
      },
      () => setTemplates([])
    );
    const unsubSnip = onSnapshot(
      collection(db, "whatsappSnippets"),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.label || a.text || "").localeCompare(b.label || b.text || ""));
        setSnippets(list);
      },
      () => setSnippets([])
    );
    return () => { unsub(); unsubSnip(); };
  }, [allowed]);

  // Only APPROVED templates can actually be sent through Meta. Strict check so
  // a stale/manually-seeded doc without a status can never leak into the picker.
  const approvedTemplates = useMemo(
    () => templates.filter((t) => t.status === "APPROVED"),
    [templates]
  );

  const syncTemplates = async () => {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const call = httpsCallable(functions, "syncWhatsappTemplates");
      const res: any = await call();
      const { total = 0, approved = 0, removed = 0 } = res.data || {};
      setSyncMsg({
        type: "ok",
        text: `Sincronizado con Meta: ${total} plantilla(s), ${approved} aprobada(s)${removed ? `, ${removed} eliminada(s)` : ""}.`,
      });
    } catch (e: any) {
      setSyncMsg({
        type: "err",
        text: e?.message ? `No se pudo sincronizar: ${e.message}` : "No se pudo sincronizar con Meta.",
      });
    } finally {
      setSyncing(false);
    }
  };

  // Predefined texts (snippets) — shared for the whole team.
  const addSnippet = async (label: string, text: string) => {
    if (!text.trim()) return;
    await addDoc(collection(db, "whatsappSnippets"), {
      label: label.trim() || null,
      text: text.trim(),
      order: snippets.length,
      by: authUser?.email || authUser?.uid || null,
      createdAt: serverTimestamp(),
    });
  };
  const deleteSnippet = async (id: string) => {
    await deleteDoc(doc(db, "whatsappSnippets", id));
  };
  const updateSnippet = async (id: string, label: string, text: string) => {
    await updateDoc(doc(db, "whatsappSnippets", id), {
      label: label.trim() || null,
      text: text.trim(),
      updatedBy: authUser?.email || authUser?.uid || null,
      updatedAt: serverTimestamp(),
    });
  };

  const selected = useMemo(
    () => threads.find((t) => t.id === selectedId) || (extraThread && extraThread.id === selectedId ? extraThread : null),
    [threads, selectedId, extraThread]
  );

  // Only the owner for the thread's CURRENT area may clear its unread state.
  // Keeping the active owner fields in the dependencies also makes an open chat
  // react immediately to reassignment or to a Comercial/Operaciones handoff.
  useEffect(() => {
    if (!selected?.id || !selected.hasUnread) return;
    if (!canMarkWhatsappThreadRead(selected, authUser?.email)) return;
    actualizarChat(selected.id, { hasUnread: false }).catch(() => {});
  }, [
    selected?.id,
    selected?.hasUnread,
    selected?.area,
    selected?.assignedComercial,
    selected?.assignedOperaciones,
    authUser?.email,
  ]);

  // El mismo atajo al formulario largo que existe en ANDI. Se busca solo para
  // el chat abierto y se valida el telefono completo antes de mostrarlo, para
  // no enlazar por error a otro cliente con un prefijo parecido.
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
  const selectedPhone = selected?.phone || "";
  // Legacy chats can have an inbound message outside the first loaded page.
  useEffect(() => {
    if (!selectedId || selected?.lastInboundAt) return;
    let active = true;
    getDocs(query(collection(db, "whatsappThreads", selectedId, "messages"),
      where("direction", "==", "inbound"), orderBy("at", "desc"), limit(1)))
      .then((snap) => { if (active) setLegacyInbound({ id: selectedId, at: toMillis(snap.docs[0]?.data().at) }); })
      .catch(() => {});
    return () => { active = false; };
  }, [selectedId, selected?.lastInboundAt]);
  useEffect(() => {
    const key = phoneKey(selectedPhone);
    setSelectedFormId(null);
    if (!key) return;

    let cancelled = false;
    const findForm = async () => {
      // El cruce teléfono -> formulario ya lo resuelve el servidor y queda
      // escrito en el lead (`formularioCofidis`), puesto por los triggers de
      // functions/src/leadFormLink.js. Una lectura por clave exacta sustituye a
      // las hasta seis consultas que había aquí. Este bloque era una copia del
      // de ANDI y, a diferencia de aquel, ni siquiera tenía caché.
      try {
        const porLead = await getDocs(query(
          collection(db, "crmLeads"),
          where("telefonoKey", "==", key),
          limit(1)
        ));
        const lead = porLead.docs[0];
        const enlace = lead && (lead.data() as any).formularioCofidis;
        if (enlace && enlace.id) {
          if (!cancelled) setSelectedFormId(String(enlace.id));
          return;
        }
        // Con la clave puesta el servidor ya buscó: si no hay enlace, no hay
        // formulario para este número y no merece la pena preguntar más.
        if (lead && (lead.data() as any).telefonoKey === key) return;
      } catch {
        /* sin lead que consultar: se sigue por el camino de siempre */
      }

      // Respaldo para quien nunca llegó a ser lead del CRM (cliente orgánico).
      // Los cuatro tokens caben en una sola consulta en vez de cuatro.
      const tokens = cofidisPhoneSearchTokens(key);
      if (tokens.length) {
        try {
          const snap = await getDocs(query(
            collection(db, "cofidisCases"),
            where("searchPrefixes", "array-contains-any", tokens),
            limit(10)
          ));
          const form = snap.docs.find((candidate) => cofidisCaseHasPhone(candidate.data(), key));
          if (form) {
            if (!cancelled) setSelectedFormId(form.id);
            return;
          }
        } catch {
          /* sigue por el respaldo de formularios antiguos */
        }
      }

      // Compatibilidad con formularios antiguos sin searchPrefixes y con el
      // movil guardado con espacios o prefijo internacional.
      for (const field of ["primerTitular.movil", "segundoTitular.movil"]) {
        try {
          const snap = await getDocs(query(
            collection(db, "cofidisCases"),
            where(field, "in", cofidisPhoneExactVariants(key)),
            limit(10)
          ));
          const form = snap.docs.find((candidate) => cofidisCaseHasPhone(candidate.data(), key));
          if (form) {
            if (!cancelled) setSelectedFormId(form.id);
            return;
          }
        } catch {
          continue;
        }
      }
    };
    findForm();
    return () => { cancelled = true; };
  }, [selectedPhone]);

  // #10 server-side search: when the user types ≥3 chars, query the WHOLE
  // collection by searchPrefixes (reads only matches — cheap) instead of only
  // filtering the 150 loaded chats. Debounced.
  const serverSearch = threadSearch.trim().length >= 3;
  useEffect(() => {
    if (!allowed || !serverSearch) { setSearchResults([]); setSearching(false); return; }
    const token = searchToken(threadSearch.trim());
    if (!token) { setSearchResults([]); return; }
    setSearching(true);
    let cancelled = false;
    const h = setTimeout(async () => {
      try {
        const encontrados = await leerEspejo("whatsappThreads", buscarEnArea(tab, token)).catch(async () => {
          const snap = await getDocs(query(
            collection(db, "whatsappThreads"),
            where("area", "==", tab),
            where("searchPrefixes", "array-contains", token),
            orderBy("lastMessageAt", "desc"),
            limit(30)
          ));
          return snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        });
        if (!cancelled) setSearchResults(encontrados as Thread[]);
      } catch {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(h); };
  }, [threadSearch, tab, allowed, serverSearch]);

  // Chats antiguos bajo demanda: los 150 recientes van en vivo y el resto se
  // pide por páginas de una sola lectura cuando alguien pulsa "Ver más
  // antiguos". Así la lista no tiene tope real y no pagamos por escuchar
  // conversaciones que nadie está mirando.
  const [olderThreads, setOlderThreads] = useState<Thread[]>([]);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [noMoreOlder, setNoMoreOlder] = useState(false);
  useEffect(() => { setOlderThreads([]); setNoMoreOlder(false); }, [tab]);

  const loadOlder = async () => {
    const ultimo = olderThreads[olderThreads.length - 1] || threads[threads.length - 1];
    if (!ultimo?.lastMessageAt || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const pagina = (await leerEspejo("whatsappThreads", anterioresDeArea(tab, toMillis(ultimo.lastMessageAt)))
        .catch(async () => {
          const snap = await getDocs(query(
            collection(db, "whatsappThreads"),
            where("area", "==", tab),
            orderBy("lastMessageAt", "desc"),
            startAfter(ultimo.lastMessageAt),
            limit(100)
          ));
          return snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        })) as Thread[];
      setOlderThreads((prev) => [...prev, ...pagina]);
      if (pagina.length < 100) setNoMoreOlder(true);
    } catch {
      setNoMoreOlder(true);
    } finally {
      setLoadingOlder(false);
    }
  };

  // Filtered thread list. When searching, the server already matched name/phone,
  // so we only apply the unread / commercial toggles on top.
  const visibleThreads = useMemo(() => {
    const vivos = threads.map((t) => t.id);
    const base = serverSearch
      ? searchResults
      : unreadOnly
        ? unreadThreadsForArea(unreadThreads, tab)
        : [...threads, ...olderThreads.filter((t) => !vivos.includes(t.id))];
    return base.filter((t) => {
      if (unreadOnly && !t.hasUnread) return false;
      if (!matchesAssignmentFilter(t, comercialFilter)) return false;
      return true;
    });
  }, [threads, olderThreads, searchResults, serverSearch, unreadOnly, unreadThreads, tab, comercialFilter]);

  // #10: messages filtered when searching within a conversation.
  const visibleMessages = useMemo(() => {
    const q = chatSearch.trim().toLowerCase();
    if (!q) return messages;
    return messages.filter((m) => (m.body || "").toLowerCase().includes(q) || (m.templateName || "").toLowerCase().includes(q));
  }, [messages, chatSearch]);

  // Reset per-chat UI state when switching conversation.
  useEffect(() => {
    setEditingName(false);
    setNameDraft(selected?.name || "");
    setChatSearch("");
    setShowChatSearch(false);
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Meta 24h customer-service window: free text only delivers if the client
  // messaged us in the last 24h; otherwise the chat is "disabled" (templates only).
  const lastInboundAt = useMemo(() => {
    let m = Math.max(toMillis(selected?.lastInboundAt), legacyInbound?.id === selectedId ? legacyInbound.at : 0);
    for (const msg of messages) if (msg.direction === "inbound") m = Math.max(m, toMillis(msg.at));
    return m;
  }, [messages, selected?.lastInboundAt, legacyInbound, selectedId]);
  const withinWindow = lastInboundAt > 0 && Date.now() - lastInboundAt < DAY_MS;

  const lastTemplateAt = useMemo(() => {
    let m = toMillis(selected?.lastTemplateAt);
    for (const msg of messages) {
      if (msg.direction === "outbound" && msg.templateName) m = Math.max(m, toMillis(msg.at));
    }
    return m;
  }, [messages, selected?.lastTemplateAt]);
  const isCommercialSender = waGroups.includes("comercial");
  const snippetsVisibles = useMemo(
    () => snippets.filter((snippet) => !snippet.area || snippet.area === tab),
    [snippets, tab]
  );
  const templateDecision = useMemo(() => decideTemplateSending({
    isCommercial: isCommercialSender,
    templateName,
    lastInboundAt,
    lastTemplateAt,
    now,
  }), [isCommercialSender, templateName, lastInboundAt, lastTemplateAt, now]);
  const selectedTemplatePreview = templateName
    ? templates.find((t) => t.name === templateName)?.preview?.trim() || ""
    : "";

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  // ── Actions ──────────────────────────────────────────────────────────
  const handleLogin = async () => {
    setLoggingIn(true);
    setLoginErr("");
    try {
      await signInWithEmailAndPassword(auth, loginEmail.trim(), loginPass);
    } catch {
      setLoginErr("No se pudo iniciar sesión. Revisa email y contraseña.");
    } finally {
      setLoggingIn(false);
    }
  };

  const me = authUser?.email || authUser?.uid || "";

  // Append a system event to the timeline (audit trail: who did what, when).
  const logEvent = async (threadId: string, event: string, extra: any = {}) => {
    await nuevoMensaje(threadId, {
      direction: "system",
      event,
      body: event,
      by: me,
      at: serverTimestamp(),
      ...extra,
    });
  };

  // First real contact moves a brand-new lead to "En comunicación" automatically.
  const touchContacted = async (t: Thread) => {
    if ((t.status || "nuevo") === "nuevo") {
      await actualizarChat(t.id, {
        status: "en_comunicacion",
        statusBy: me,
        statusAt: serverTimestamp(),
      });
      await logEvent(t.id, "Estado → En comunicación");
    }
  };

  // Marcar como no leído: devuelve el punto rojo y cierra el chat para que
  // abrirlo de nuevo no lo vuelva a marcar como leído al instante.
  const markUnread = () => {
    if (!selectedId) return;
    actualizarChat(selectedId, { hasUnread: true }).catch(() => {});
    setSelectedId(null);
  };

  const changeStatus = async (newStatus: string) => {
    if (!selected || newStatus === (selected.status || "nuevo")) return;
    try {
      await actualizarChat(selected.id, {
        status: newStatus,
        statusBy: me,
        statusAt: serverTimestamp(),
      });
      await logEvent(selected.id, `Estado → ${statusOf(newStatus).label}`);
    } catch {
      setActionErr("No se pudo cambiar el estado.");
    }
  };

  const sendTemplate = async () => {
    if (!selected || !templateName) return;
    if (templateDecision.kind === "blocked") {
      setActionErr("No se puede enviar otra plantilla todavía. Deben pasar 24 horas desde la anterior.");
      return;
    }
    if (templateDecision.kind === "text" && !selectedTemplatePreview) {
      setActionErr("Esta plantilla no tiene texto disponible para enviarla dentro de la conversación abierta.");
      return;
    }
    setSending(true);
    setActionErr("");
    try {
      const tpl = templates.find((t) => t.name === templateName);
      const asText = templateDecision.kind === "text";
      await nuevoMensaje(selected.id, asText ? {
        direction: "outbound",
        status: "pending",
        body: selectedTemplatePreview,
        at: serverTimestamp(),
        by: me,
      } : {
        direction: "outbound",
        status: "pending",
        templateName,
        language: tpl?.language || "es",
        at: serverTimestamp(),
        by: me,
      });
      await actualizarChat(selected.id, {
        lastMessageAt: serverTimestamp(),
        ...(asText
          ? { lastMessagePreview: selectedTemplatePreview.slice(0, 80) }
          : { lastMessagePreview: `📋 ${templateName}`, lastTemplateAt: serverTimestamp() }),
        lastAgent: me,
        lastAgentAt: serverTimestamp(),
        lastMessageDirection: "outbound",
      });
      await touchContacted(selected);
      setTemplateName("");
    } catch (e: any) {
      setActionErr("No se pudo enviar la plantilla.");
    } finally {
      setSending(false);
    }
  };

  const sendText = async () => {
    if (!selected || !text.trim()) return;
    setSending(true);
    setActionErr("");
    try {
      const body = text.trim();
      await nuevoMensaje(selected.id, {
        direction: "outbound",
        status: "pending",
        body,
        at: serverTimestamp(),
        by: me,
      });
      await actualizarChat(selected.id, {
        lastMessageAt: serverTimestamp(),
        lastMessagePreview: body.slice(0, 80),
        lastAgent: me,
        lastAgentAt: serverTimestamp(),
        lastMessageDirection: "outbound",
      });
      await touchContacted(selected);
      setText("");
      if (composerRef.current) composerRef.current.style.height = "auto";
    } catch {
      setActionErr("No se pudo enviar el mensaje.");
    } finally {
      setSending(false);
    }
  };

  // Send any attachment (image, audio/voice note, or generic file).
  const sendImage = async (file: File) => {
    if (!selected || !file) return;
    setUploading(true);
    setActionErr("");
    try {
      const kind = file.type.startsWith("image/") ? "image" : file.type.startsWith("audio/") ? "audio" : "file";
      const path = `whatsapp/${selected.id}/outbound/${Date.now()}-${file.name}`;
      const r = storageRef(storage, path);
      await uploadBytes(r, file, { contentType: file.type });
      const url = await getDownloadURL(r);
      await nuevoMensaje(selected.id, {
        direction: "outbound",
        status: "pending",
        attachments: [{ kind, downloadUrl: url, contentType: file.type, name: file.name }],
        at: serverTimestamp(),
        by: me,
      });
      await actualizarChat(selected.id, {
        lastMessageAt: serverTimestamp(),
        lastMessagePreview: kind === "image" ? "📷 Imagen" : kind === "audio" ? "🎤 Audio" : `📎 ${file.name}`,
        lastAgent: me,
        lastAgentAt: serverTimestamp(),
        lastMessageDirection: "outbound",
      });
    } catch {
      setActionErr("No se pudo enviar el archivo.");
    } finally {
      setUploading(false);
    }
  };

  // Voice-note recording (MediaRecorder). On stop we keep it as a PREVIEW so the
  // agent can listen before sending; sending converts to MP3 (Meta rejects webm).
  const startRecording = async () => {
    setActionErr("");
    if (pendingAudio) { URL.revokeObjectURL(pendingAudio.url); setPendingAudio(null); }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const types = ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/mp4", "audio/webm"];
      const mimeType = types.find((t) => (window as any).MediaRecorder?.isTypeSupported?.(t)) || "";
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) audioChunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = mr.mimeType || "audio/webm";
        const blob = new Blob(audioChunksRef.current, { type });
        setPendingAudio({ blob, url: URL.createObjectURL(blob), type });
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      setActionErr("No se pudo acceder al micrófono.");
    }
  };
  const stopRecording = () => {
    try { mediaRecorderRef.current?.stop(); } catch { /* noop */ }
    setRecording(false);
  };
  const discardAudio = () => {
    if (pendingAudio) URL.revokeObjectURL(pendingAudio.url);
    setPendingAudio(null);
  };

  // Convert the recorded blob to MP3 (audio/mpeg — a format WhatsApp accepts).
  const toMp3 = async (blob: Blob): Promise<File> => {
    const mod: any = await import("@breezystack/lamejs");
    const Mp3Encoder = mod.Mp3Encoder || (mod.default && mod.default.Mp3Encoder);
    const buf = await blob.arrayBuffer();
    const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
    const ac = new AC();
    const audio = await ac.decodeAudioData(buf);
    const ch = audio.getChannelData(0);
    const samples = new Int16Array(ch.length);
    for (let i = 0; i < ch.length; i++) {
      const s = Math.max(-1, Math.min(1, ch[i]));
      samples[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    const enc = new Mp3Encoder(1, audio.sampleRate, 128);
    const out: Uint8Array[] = [];
    for (let i = 0; i < samples.length; i += 1152) {
      const b = enc.encodeBuffer(samples.subarray(i, i + 1152));
      if (b.length) out.push(b);
    }
    const end = enc.flush();
    if (end.length) out.push(end);
    ac.close();
    return new File(out as any, `nota-voz-${Date.now()}.mp3`, { type: "audio/mpeg" });
  };

  const sendPendingAudio = async () => {
    if (!pendingAudio) return;
    setUploading(true);
    setActionErr("");
    try {
      const mp3 = await toMp3(pendingAudio.blob);
      discardAudio();
      await sendImage(mp3);
    } catch {
      setActionErr("No se pudo convertir el audio. Adjunta un archivo mp3/ogg con el clip.");
      setUploading(false);
    }
  };

  const assignToOperaciones = async (note: string) => {
    if (!selected) return;
    setActionErr("");
    try {
      const threadRef = doc(db, "whatsappThreads", selected.id);
      const owner = await runTransaction(db, async (tx) => {
        const current = await tx.get(threadRef);
        if (!current.exists()) throw new Error("El chat ya no existe");

        const linkedLeadId = crmLeadIdForWhatsappThread(current.data());
        let linkedAnalyst = "";
        if (linkedLeadId) {
          const lead = await tx.get(doc(db, "crmLeads", linkedLeadId));
          linkedAnalyst = lead.exists() ? String(lead.get("analista") || "") : "";
        }

        const handoff = operationsHandoffPatch(current.data(), linkedAnalyst);
        const cambios = {
          ...handoff,
          assignedAt: serverTimestamp(),
          assignedBy: me,
          handoffNote: note || null,
        };
        tx.update(threadRef, cambios);
        return { cambios, owner: handoff.assignedOperaciones || String(current.get("assignedOperaciones") || "") };
      }).then(({ cambios, owner }) => {
        // Con el espejo, que salga ya de la bandeja de Clientes.
        parcheLocal("whatsappThreads", selected.id, cambios);
        return owner;
      });
      // Audit event visible to Operaciones with the handoff context.
      await logEvent(
        selected.id,
        owner ? `Derivado a Operaciones: ${owner}` : "Derivado a Operaciones",
        note ? { note } : {},
      );
      setShowHandoff(false);
      setHandoffNote("");
      // it leaves the commercial inbox
      setSelectedId(null);
    } catch {
      setActionErr("No se pudo asignar a Operaciones.");
    }
  };

  const returnToComercial = async () => {
    if (!selected || !canReturnToClients) return;
    setActionErr("");
    try {
      const devolver = httpsCallable(functions, "returnWhatsappToComercial");
      await devolver({ threadId: selected.id });
      setSelectedId(null);
    } catch {
      setActionErr("No se pudo devolver el chat a Clientes.");
    }
  };

  // #9 Rename the client's display name.
  const saveName = async () => {
    if (!selected) return;
    const newName = nameDraft.trim();
    const finalName = newName || selected.phone || "";
    try {
      await actualizarChat(selected.id, {
        name: finalName,
        searchPrefixes: buildThreadPrefixes(selected.phone || "", finalName),
      });
      setEditingName(false);
    } catch {
      setActionErr("No se pudo cambiar el nombre.");
    }
  };

  // #5 Assign a person from a separate dropdown (comercial / operaciones).
  const setAssigned = async (field: "assignedComercial" | "assignedOperaciones", person: string) => {
    if (!selected) return;
    try {
      await actualizarChat(selected.id, { [field]: person || null });
      const label = field === "assignedComercial" ? "Comercial" : "Operaciones";
      await logEvent(selected.id, person ? `Asignado (${label}): ${person}` : `Quitada asignación de ${label}`);
    } catch {
      setActionErr("No se pudo asignar.");
    }
  };

  // #1 Start a brand-new chat: create the thread and queue an approved template.
  // Bug fix (Diego): dedupe against the WHOLE collection (any area), not just the
  // chats loaded in the current tab — otherwise a number that already exists in
  // Operaciones would spawn a duplicate empty chat in Comercial.
  const startNewChat = async (phoneRaw: string, name: string, tplName: string) => {
    const d = phoneRaw.replace(/\D/g, "");
    const phone = d.length === 9 && /^[679]/.test(d) ? "34" + d : d;
    if (!phone) throw new Error("Teléfono no válido");
    // Look up any existing thread with this phone across all areas.
    const existingSnap = await getDocs(
      query(collection(db, "whatsappThreads"), where("phone", "==", phone), limit(1))
    );
    // Existing chat (any area): just open it with its full history — never a duplicate.
    if (!existingSnap.empty) {
      const exDoc = existingSnap.docs[0];
      const exArea = (exDoc.data() as any).area || "comercial";
      if (exArea !== tab) setTab(exArea);
      setShowNewChat(false);
      setSelectedId(exDoc.id);
      return;
    }
    // Brand-new number → create the thread and (optionally) send a template.
    const refNew = await addDoc(collection(db, "whatsappThreads"), {
      phone,
      name: name.trim() || phone,
      searchPrefixes: buildThreadPrefixes(phone, name.trim() || phone),
      area: tab,
      status: "nuevo",
      source: "manual",
      hasUnread: false,
      unread: { comercial: 0, operaciones: 0 },
      lastMessageAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    });
    const threadId = refNew.id;
    await logEvent(threadId, "Chat iniciado manualmente");
    if (tplName) {
      const tpl = templates.find((t) => t.name === tplName);
      await nuevoMensaje(threadId, {
        direction: "outbound",
        status: "pending",
        templateName: tplName,
        language: tpl?.language || "es",
        at: serverTimestamp(),
        by: me,
      });
      await actualizarChat(threadId, {
        lastMessageAt: serverTimestamp(),
        lastMessagePreview: `📋 ${tplName}`,
        lastAgent: me,
        lastMessageDirection: "outbound",
      });
    }
    setShowNewChat(false);
    setSelectedId(threadId);
  };

  // ── Render ───────────────────────────────────────────────────────────
  if (checking) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Cargando…</p>
      </main>
    );
  }

  if (!authUser) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
        <div className="w-full max-w-sm rounded-2xl bg-card p-8 shadow-xl">
          <img src={easyDebtLogo} alt="EasyDebt" className="mx-auto mb-6 h-9 w-auto" />
          <h1 className="mb-6 text-center text-xl font-bold text-foreground">WhatsApp · EasyDebt</h1>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Email</label>
              <input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Contraseña</label>
              <input type="password" value={loginPass} onChange={(e) => setLoginPass(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleLogin()} className={inputClass} />
            </div>
            <button onClick={handleLogin} disabled={loggingIn} className={`w-full ${btnPrimary}`}>
              {loggingIn ? "Entrando…" : "Entrar"}
            </button>
            {loginErr && <p className="text-sm text-destructive">{loginErr}</p>}
          </div>
        </div>
      </main>
    );
  }

  if (!allowed) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm rounded-2xl bg-card p-8 text-center shadow-xl">
          <h1 className="mb-2 text-xl font-bold text-foreground">Sin acceso</h1>
          <p className="mb-6 text-sm text-muted-foreground">
            {authUser.email} no pertenece a ningún grupo de WhatsApp.
          </p>
          <button onClick={() => signOut(auth)} className={`w-full ${btnPrimary}`}>Cerrar sesión</button>
        </div>
      </main>
    );
  }

  const tabs: Area[] = [];
  if (canComercial) tabs.push("comercial");
  if (canOperaciones) tabs.push("operaciones");

  return (
    <main className="flex h-screen flex-col bg-background">
      {/* Header */}
      <header className="flex items-center justify-between border-b border-border bg-card px-4 py-3">
        <div className="flex items-center gap-3">
          <img src={easyDebtLogo} alt="EasyDebt" className="h-6 w-auto" />
          <span className="text-base font-bold text-foreground">WhatsApp</span>
          {isAdmin && <span className="rounded bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">Admin</span>}
          <nav className="ml-2 flex gap-1">
            <button
              onClick={() => setView("chats")}
              className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
                view === "chats" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"
              }`}
            >
              Chats
            </button>
            {isAdmin && (
              <button
                onClick={() => setView("snippets")}
                className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
                  view === "snippets" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"
                }`}
              >
                Textos
              </button>
            )}
            {isAdmin && (
              <button
                onClick={() => setView("templates")}
                className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
                  view === "templates" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"
                }`}
              >
                Plantillas
              </button>
            )}
          </nav>
        </div>
        <div className="flex items-center gap-3">
          {(() => {
            const total = (canComercial ? unreadByArea.comercial : 0) + (canOperaciones ? unreadByArea.operaciones : 0);
            return (
              <button
                onClick={() => { setView("chats"); setUnreadOnly((v) => !v); }}
                className={`relative rounded-lg px-2 py-1 transition-colors hover:bg-secondary ${unreadOnly ? "bg-secondary" : ""}`}
                title={unreadOnly ? "Mostrando solo no leídos — clic para ver todos" : "No leídos — clic para filtrar"}
              >
                <span className="text-xl">🔔</span>
                {total > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                    {total > 99 ? "99+" : total}
                  </span>
                )}
              </button>
            );
          })()}
          <span className="hidden text-xs text-muted-foreground sm:inline">{authUser.email}</span>
          <button onClick={toggleDark} title={dark ? "Modo claro" : "Modo oscuro"} aria-label="Cambiar tema"
            className="rounded-lg border border-border bg-card px-2.5 py-1.5 text-sm text-foreground hover:bg-secondary">
            {dark ? "☀️" : "🌙"}
          </button>
          <button onClick={() => signOut(auth)}
            className="rounded-lg border border-border bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-secondary">
            Salir
          </button>
        </div>
      </header>

      {view === "templates" && isAdmin ? (
        <TemplatesPanel
          templates={templates}
          syncing={syncing}
          syncMsg={syncMsg}
          onSync={syncTemplates}
        />
      ) : view === "snippets" && isAdmin ? (
        <SnippetsPanel snippets={snippets} onAdd={addSnippet} onDelete={deleteSnippet} onUpdate={updateSnippet} />
      ) : (
      <div className="flex min-h-0 flex-1">
        {/* Thread list */}
        <aside className="flex w-full max-w-xs flex-col border-r border-border bg-card sm:w-80">
          <div className="flex border-b border-border">
            {tabs.map((t) => (
              <button key={t} onClick={() => { setTab(t); setSelectedId(null); }}
                className={`flex flex-1 items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold transition-colors ${
                  tab === t ? "border-b-2 border-primary text-primary" : "text-muted-foreground hover:text-foreground"
                }`}>
                {AREA_LABEL[t]}
                {unreadByArea[t] > 0 && (
                  <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                    {unreadByArea[t]}
                  </span>
                )}
              </button>
            ))}
          </div>
          {/* Search + new chat + unread filter */}
          <div className="space-y-2 border-b border-border p-2">
            <div className="flex gap-2">
              <div className="relative min-w-0 flex-1">
                <input
                  type="text"
                  value={threadSearch}
                  onChange={(e) => setThreadSearch(e.target.value)}
                  placeholder="Buscar por número o nombre…"
                  className="w-full rounded-lg border border-border bg-card px-3 py-1.5 pr-7 text-xs text-foreground outline-none focus:border-primary"
                />
                {threadSearch && (
                  <button onClick={() => setThreadSearch("")} title="Borrar búsqueda"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full px-1 text-sm leading-none text-muted-foreground hover:text-foreground">
                    ✕
                  </button>
                )}
              </div>
              <button onClick={() => { setNewChatPhone(""); setShowNewChat(true); }} title="Nuevo chat"
                className="shrink-0 rounded-lg bg-primary px-3 py-1.5 text-sm font-bold text-primary-foreground hover:opacity-90">
                + Nuevo
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setUnreadOnly((v) => !v)}
                className={`flex-1 rounded-lg px-3 py-1 text-xs font-semibold transition-colors ${
                  unreadOnly ? "bg-destructive text-destructive-foreground" : "border border-border bg-card text-muted-foreground hover:bg-secondary"
                }`}
              >
                {unreadOnly ? "No leídos ✓" : "Ver no leídos"}
              </button>
              {/* Filter by assigned commercial (Andre's request) */}
              <select
                value={comercialFilter}
                onChange={(e) => setComercialFilter(e.target.value)}
                title="Filtrar por comercial"
                className={`min-w-0 flex-1 rounded-lg border px-2 py-1 text-xs outline-none focus:border-primary ${
                  comercialFilter ? "border-primary bg-primary/5 font-semibold text-primary" : "border-border bg-card text-muted-foreground"
                }`}
              >
                <option value="">Todas las asignaciones</option>
                <option value={UNASSIGNED_FILTER}>Sin asignados</option>
                {ASSIGNEES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {visibleThreads.length === 0 ? (
              <p className="p-4 text-center text-sm text-muted-foreground">
                {searching ? "Buscando…" : serverSearch ? "Sin resultados." : unreadOnly ? "No hay chats sin responder." : `No hay chats en ${AREA_LABEL[tab]}.`}
              </p>
            ) : (
              visibleThreads.map((t) => {
                const st = statusOf(t.status);
                const unread = !!t.hasUnread;
                const assigned = [t.assignedComercial, t.assignedOperaciones, ...(Array.isArray(t.assignedComerciales) ? t.assignedComerciales : [])].filter(Boolean) as string[];
                return (
                <button key={t.id} onClick={() => setSelectedId(t.id)}
                  className={`w-full border-b border-border/60 px-4 py-3 text-left transition-colors hover:bg-secondary ${
                    selectedId === t.id ? "bg-secondary" : ""
                  }`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex min-w-0 items-center gap-1.5">
                      {unread && <span className="h-2 w-2 shrink-0 rounded-full bg-destructive" title="Sin responder" />}
                      {/* #9 number first, then name */}
                      <span className="truncate text-sm font-semibold text-foreground">
                        {t.phone || "—"}
                        {t.name && t.name !== t.phone ? <span className="font-normal text-muted-foreground"> · {t.name}</span> : ""}
                      </span>
                    </span>
                    <span className="shrink-0 text-[10px] text-muted-foreground">{fmtTime(t.lastMessageAt)}</span>
                  </div>
                  <div className="truncate text-xs text-muted-foreground">{t.lastMessagePreview || ""}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${st.color}`}>{st.label}</span>
                    {assigned.map((p) => (
                      <span key={p} className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">{p}</span>
                    ))}
                  </div>
                </button>
                );
              })
            )}
            {!serverSearch && !noMoreOlder && threads.length >= 150 && (
              <button onClick={loadOlder} disabled={loadingOlder}
                className="w-full px-4 py-3 text-center text-xs font-semibold text-primary hover:bg-secondary disabled:opacity-50">
                {loadingOlder ? "Cargando…" : "Ver chats más antiguos"}
              </button>
            )}
          </div>
        </aside>

        {/* Chat panel */}
        <section className="flex min-w-0 flex-1 flex-col bg-background">
          {!selected ? (
            <div className="flex flex-1 items-center justify-center">
              <p className="text-sm text-muted-foreground">Selecciona un chat</p>
            </div>
          ) : (
            <>
              <div className="border-b border-border bg-card px-4 py-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <div className="min-w-0">
                      {/* #9 number first, then editable name */}
                      <div className="truncate text-sm font-bold text-foreground">{selected.phone}</div>
                      {editingName ? (
                        <div className="mt-1 flex items-center gap-1">
                          <input
                            type="text"
                            value={nameDraft}
                            onChange={(e) => setNameDraft(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }}
                            autoFocus
                            placeholder="Nombre del cliente"
                            className="min-w-0 flex-1 rounded border border-border bg-card px-2 py-0.5 text-xs text-foreground outline-none focus:border-primary"
                          />
                          <button onClick={saveName} className="rounded bg-primary px-2 py-0.5 text-[10px] font-bold text-primary-foreground">OK</button>
                          <button onClick={() => setEditingName(false)} className="rounded border border-border px-2 py-0.5 text-[10px] text-muted-foreground">✕</button>
                        </div>
                      ) : (
                        <button onClick={() => { setNameDraft(selected.name || ""); setEditingName(true); }}
                          className="group flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" title="Editar nombre">
                          <span className="truncate">{selected.name && selected.name !== selected.phone ? selected.name : "Añadir nombre"}</span>
                          <span className="opacity-0 group-hover:opacity-100">✎</span>
                        </button>
                      )}
                    </div>
                    {selectedFormId && (
                      <a
                        href={clientFormUrl(selectedFormId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#323338] text-sm font-bold text-white hover:opacity-80"
                        title="Abrir formulario del cliente"
                        aria-label={`Abrir formulario de ${selected.name || selected.phone || "cliente"}`}
                      >
                        ↗
                      </a>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {/* Marcar como no leído (petición de André) */}
                    <button onClick={markUnread} title="Marcar como no leído — el chat vuelve a salir con el punto rojo y en 'Ver no leídos'"
                      className="rounded-lg border border-border bg-card px-2 py-1.5 text-xs font-medium text-muted-foreground hover:bg-secondary">
                      📩 No leído
                    </button>
                    {/* #10 search within conversation */}
                    <button onClick={() => setShowChatSearch((v) => !v)} title="Buscar en la conversación"
                      className={`rounded-lg border border-border px-2 py-1.5 text-sm hover:bg-secondary ${showChatSearch ? "bg-secondary" : "bg-card"}`}>
                      🔍
                    </button>
                    {/* Pipeline status (editable) */}
                    <select
                      value={selected.status || "nuevo"}
                      onChange={(e) => changeStatus(e.target.value)}
                      className="rounded-lg border border-border bg-card px-2 py-1.5 text-xs font-semibold text-foreground outline-none focus:border-primary"
                      title="Estado del cliente"
                    >
                      {STATUSES.map((s) => (
                        <option key={s.id} value={s.id}>{s.label}</option>
                      ))}
                    </select>
                    {tab === "comercial" && (
                      <button onClick={() => { setHandoffNote(""); setShowHandoff(true); }}
                        className="rounded-lg bg-accent px-3 py-2 text-xs font-semibold text-accent-foreground hover:opacity-90">
                        Asignar a Operaciones →
                      </button>
                    )}
                    {tab === "operaciones" && canReturnToClients && (
                      <button onClick={returnToComercial}
                        className="rounded-lg bg-accent px-3 py-2 text-xs font-semibold text-accent-foreground hover:opacity-90">
                        ← Devolver a Clientes
                      </button>
                    )}
                  </div>
                </div>
                {/* #10 search box within the conversation */}
                {showChatSearch && (
                  <div className="relative mt-2">
                    <input
                      type="text"
                      value={chatSearch}
                      onChange={(e) => setChatSearch(e.target.value)}
                      autoFocus
                      placeholder="Buscar en esta conversación…"
                      className="w-full rounded-lg border border-border bg-card px-3 py-1.5 pr-7 text-xs text-foreground outline-none focus:border-primary"
                    />
                    {chatSearch && (
                      <button onClick={() => setChatSearch("")} title="Borrar búsqueda"
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full px-1 text-sm leading-none text-muted-foreground hover:text-foreground">
                        ✕
                      </button>
                    )}
                  </div>
                )}
                {/* #5 assign via two separate dropdowns (comercial / operaciones) */}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <label className="flex items-center gap-1 text-[10px] font-semibold text-muted-foreground">
                    Comercial:
                    <select
                      value={selected.assignedComercial || ""}
                      onChange={(e) => setAssigned("assignedComercial", e.target.value)}
                      className="rounded-md border border-border bg-card px-2 py-1 text-[11px] text-foreground outline-none focus:border-primary"
                    >
                      <option value="">— sin asignar —</option>
                      {Array.from(new Set([...COMERCIALES, ...(selected.assignedComercial ? [selected.assignedComercial] : [])]))
                        .map((p) => (<option key={p} value={p}>{p}</option>))}
                    </select>
                  </label>
                  <label className="flex items-center gap-1 text-[10px] font-semibold text-muted-foreground">
                    Operaciones:
                    <select
                      value={selected.assignedOperaciones || ""}
                      onChange={(e) => setAssigned("assignedOperaciones", e.target.value)}
                      className="rounded-md border border-border bg-card px-2 py-1 text-[11px] text-foreground outline-none focus:border-primary"
                    >
                      <option value="">— sin asignar —</option>
                      {/* Keep an assignee that is no longer on the list visible, so a
                          chat assigned before does not look unassigned. */}
                      {Array.from(new Set([...OPERACIONES_PEOPLE, ...(selected.assignedOperaciones ? [selected.assignedOperaciones] : [])])).map((p) => (<option key={p} value={p}>{p}</option>))}
                    </select>
                  </label>
                </div>
                {/* Tags: group, source, client data, assignment trail */}
                <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px]">
                  <span className={`rounded-full px-2 py-0.5 font-semibold ${selected.area === "operaciones" ? "bg-teal-500/15 text-teal-600" : "bg-primary/10 text-primary"}`}>
                    {AREA_LABEL[selected.area || "comercial"]}
                  </span>
                  <span className={`rounded-full px-2 py-0.5 font-semibold ${statusOf(selected.status).color}`}>
                    {statusOf(selected.status).label}
                  </span>
                  {selected.source && (
                    <span className="rounded-full bg-secondary px-2 py-0.5 text-muted-foreground">
                      {selected.source === "form-simple" ? "📋 Formulario" : selected.source === "whatsapp" ? "💬 WhatsApp" : selected.source}
                    </span>
                  )}
                  {(selected as any).importeSolicitado && (
                    <span className="rounded-full bg-secondary px-2 py-0.5 text-muted-foreground">💶 {(selected as any).importeSolicitado}</span>
                  )}
                  {(selected as any).motivoPrestamo && (
                    <span className="rounded-full bg-secondary px-2 py-0.5 text-muted-foreground">{(selected as any).motivoPrestamo}</span>
                  )}
                  {(selected as any).lastAgent && (
                    <span className="text-muted-foreground">· última gestión: <strong className="text-foreground">{shortName((selected as any).lastAgent)}</strong></span>
                  )}
                  {selected.assignedBy && (
                    <span className="text-muted-foreground">· derivado por <strong className="text-foreground">{shortName(selected.assignedBy)}</strong> {fmtTime(selected.assignedAt)}</span>
                  )}
                </div>
                {(selected as any).handoffNote && tab === "operaciones" && (
                  <div className="mt-2 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-foreground">
                    <span className="font-semibold text-amber-700">Nota de traspaso:</span> {(selected as any).handoffNote}
                  </div>
                )}
              </div>

              <div ref={listaMsgRef}
                onScroll={() => {
                  pegadoRef.current = pegadoAbajo(listaMsgRef.current);
                  if ((listaMsgRef.current?.scrollTop || 0) < 80 && !chatSearch) loadOlderMessages();
                }}
                className="flex-1 space-y-2 overflow-y-auto p-4">
                {(moreMessages || loadingMessages || messagesError) && (
                  <div className="text-center text-xs text-muted-foreground">
                    {messagesError ? <button onClick={() => messagePages.current?.retry()} className="underline">No se pudieron cargar los mensajes. Reintentar</button>
                      : loadingMessages ? <span role="status">{chatSearch ? "Buscando en mensajes anteriores…" : "Cargando mensajes…"}</span>
                      : <button onClick={loadOlderMessages} className="underline">Ver mensajes anteriores</button>}
                  </div>
                )}
                {visibleMessages.length === 0 ? (
                  <p className="text-center text-sm text-muted-foreground">
                    {loadingMessages || messagesError || (chatSearch && moreMessages) ? "" : chatSearch ? "Sin coincidencias en esta conversación." : "Sin mensajes todavía."}
                  </p>
                ) : (
                  visibleMessages.map((m) => {
                    // System events (audit trail): centered pill, not a bubble.
                    if (m.direction === "system") {
                      return (
                        <div key={m.id} className="flex justify-center">
                          <div className="max-w-[85%] rounded-full bg-secondary px-3 py-1 text-center text-[11px] text-muted-foreground">
                            <span className="font-semibold text-foreground">{m.event || m.body}</span>
                            {m.note ? <span> — “{m.note}”</span> : ""}
                            <span className="opacity-70"> · {shortName(m.by) || "sistema"} · {fmtTime(m.at)}</span>
                          </div>
                        </div>
                      );
                    }
                    const out = m.direction === "outbound";
                    const read = m.deliveryStatus === "read";
                    const failed = m.status === "failed" || m.deliveryStatus === "failed";
                    const tick = failed ? "⚠" :
                      read || m.deliveryStatus === "delivered" ? "✓✓" :
                      m.deliveryStatus === "sent" || m.status === "sent" ? "✓" :
                      m.status === "pending" ? "⏳" : "";
                    const failReason = m.deliveryError
                      ? `No entregado — ${m.deliveryError.title || ""}${m.deliveryError.code ? ` (${m.deliveryError.code})` : ""}${m.deliveryError.details ? `: ${m.deliveryError.details}` : ""}`
                      : failed ? "No entregado por Meta" : "";
                    return (
                    <div key={m.id} className={`flex ${out ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                        out ? "bg-primary text-primary-foreground" : "bg-card text-foreground border border-border"
                      }`}>
                        {out && m.by && m.by !== "auto" && (
                          <div className="mb-0.5 text-[10px] font-semibold opacity-80">{shortName(m.by)}</div>
                        )}
                        {out && m.by === "auto" && (
                          <div className="mb-0.5 text-[10px] font-semibold opacity-80">🤖 automático</div>
                        )}
                        {m.templateName && <div className="mb-0.5 text-[10px] font-semibold opacity-80">📋 {m.templateName}</div>}
                        {/* #2 show the actual template text so agents see what was sent */}
                        {m.templateName && !m.body && (() => {
                          const tplPrev = templates.find((t) => t.name === m.templateName)?.preview;
                          return tplPrev ? <div className="whitespace-pre-wrap">{tplPrev}</div> : null;
                        })()}
                        {(m.attachments || []).map((a, i) =>
                          a.kind === "image" && a.downloadUrl ? (
                            <a key={i} href={a.downloadUrl} target="_blank" rel="noopener noreferrer">
                              <img src={a.downloadUrl} alt={a.name || "imagen"} className="mb-1 max-h-56 rounded-lg" />
                            </a>
                          ) : a.kind === "audio" && a.downloadUrl ? (
                            <audio key={i} controls src={a.downloadUrl} className="mb-1 max-w-full" />
                          ) : a.downloadUrl ? (
                            <a key={i} href={a.downloadUrl} target="_blank" rel="noopener noreferrer"
                              className="mb-1 block underline">📎 {a.name || "archivo"}</a>
                          ) : null
                        )}
                        {m.body && <div className="whitespace-pre-wrap">{m.body}</div>}
                        <div className="mt-0.5 flex items-center justify-end gap-1 text-[10px] opacity-70">
                          <span>{fmtTime(m.at)}</span>
                          {out && <span className={read ? "rounded bg-[#25D366] px-1 font-bold text-white" : failed ? "text-destructive" : ""} title={failReason}>{tick}</span>}
                        </div>
                        {out && failed && (
                          <div className="mt-0.5 text-[10px] font-semibold text-destructive" title={failReason}>
                            ⚠ No entregado{m.deliveryError?.code ? ` (${m.deliveryError.code})` : ""}
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })
                )}
                <div ref={bottomRef} />
              </div>

              {/* Composer: free text (24h window) + approved templates */}
              <div className="space-y-2 border-t border-border bg-card p-3">
                {/* Predefined texts (snippets) */}
                {snippetsVisibles.length > 0 && (
                  <select
                    value=""
                    onChange={(e) => {
                      const s = snippets.find((x) => x.id === e.target.value);
                      if (s) setText(s.text);
                    }}
                    className="w-full rounded-lg border border-border bg-card px-3 py-1.5 text-xs text-foreground outline-none focus:border-primary"
                  >
                    <option value="">Texto predeterminado…</option>
                    {snippetsVisibles.map((s) => (
                      <option key={s.id} value={s.id}>{s.label || s.text.slice(0, 40)}</option>
                    ))}
                  </select>
                )}
                {/* Voice-note preview: listen before sending (Andre's request) */}
                {pendingAudio && (
                  <div className="flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/5 p-2">
                    <span className="text-xs font-semibold text-primary">Nota de voz</span>
                    <audio controls src={pendingAudio.url} className="h-8 min-w-0 flex-1" />
                    <button onClick={sendPendingAudio} disabled={uploading} className={btnPrimary}>
                      {uploading ? "…" : "Enviar"}
                    </button>
                    <button onClick={discardAudio} disabled={uploading}
                      className="rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-destructive hover:bg-destructive/10">
                      Descartar
                    </button>
                  </div>
                )}
                {/* Free-text message + attach — only inside Meta's 24h window */}
                {withinWindow ? (
                  <div className="flex gap-2">
                    <label className={`flex cursor-pointer items-center rounded-lg border border-border bg-card px-3 text-lg text-muted-foreground hover:bg-secondary ${uploading ? "opacity-40" : ""}`} title="Adjuntar documento, imagen o audio">
                      📎
                      {/* Documentos incluidos (PDF, Word, Excel…): WhatsApp los envía
                          como "document" y el backend ya lo soporta. */}
                      <input
                        type="file"
                        accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                        className="hidden"
                        disabled={uploading}
                        onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) sendImage(f); e.currentTarget.value = ""; }} />
                    </label>
                    {/* Voice note record */}
                    <button
                      onClick={recording ? stopRecording : startRecording}
                      disabled={uploading}
                      title={recording ? "Detener y enviar" : "Grabar nota de voz"}
                      className={`flex items-center rounded-lg border px-3 text-lg transition-colors ${
                        recording ? "animate-pulse border-destructive bg-destructive/10 text-destructive" : "border-border bg-card text-muted-foreground hover:bg-secondary"
                      }`}
                    >
                      {recording ? "⏺" : "🎤"}
                    </button>
                    {/* Textarea, no input: Enter envía y Shift+Enter hace párrafo,
                        como en WhatsApp. Crece hasta 5 líneas y luego hace scroll. */}
                    <textarea
                      ref={composerRef}
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          if (!sending && text.trim()) sendText();
                        }
                      }}
                      rows={1}
                      placeholder={recording ? "Grabando… pulsa ⏺ para enviar" : "Escribe un mensaje…  (Shift+Enter para bajar de línea)"}
                      disabled={recording}
                      className={`${inputClass} max-h-32 resize-none py-2 leading-snug`}
                      style={{ height: "auto" }}
                      onInput={(e) => {
                        const el = e.currentTarget;
                        el.style.height = "auto";
                        el.style.height = Math.min(el.scrollHeight, 128) + "px";
                      }}
                    />
                    <button onClick={sendText} disabled={sending || !text.trim()} className={btnPrimary}>
                      {sending ? "…" : "Enviar"}
                    </button>
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-border bg-secondary/40 px-3 py-2 text-[11px] text-muted-foreground">
                    🔒 Chat de texto <strong>desactivado</strong>: {lastInboundAt ? "han pasado más de 24 h desde el último mensaje del cliente." : "el cliente aún no ha escrito."} Solo puedes enviar una <strong>plantilla aprobada por Meta</strong>. El cliente se reactivará en cuanto responda.
                  </div>
                )}
                {/* Approved template (always available) */}
                <div className="flex items-center gap-2">
                  <select value={templateName} onChange={(e) => setTemplateName(e.target.value)}
                    className="min-w-0 flex-1 rounded-lg border border-border bg-card px-3 py-1.5 text-xs text-foreground outline-none focus:border-primary">
                    <option value="">Plantilla aprobada…</option>
                    {approvedTemplates.map((t) => (
                      <option key={t.id} value={t.name}>{t.name}{t.language ? ` (${t.language})` : ""}</option>
                    ))}
                  </select>
                  <button onClick={sendTemplate} disabled={sending || !templateName || templateDecision.kind === "blocked"}
                    className="shrink-0 rounded-lg border border-primary px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/5 disabled:opacity-40">
                    {templateDecision.kind === "text" ? "Enviar como texto" : "Enviar plantilla"}
                  </button>
                </div>
                {/* #2 preview of the selected template's text before sending */}
                {templateName && (() => {
                  const prev = templates.find((t) => t.name === templateName)?.preview;
                  return prev ? (
                    <div className="whitespace-pre-wrap rounded-lg bg-secondary/60 px-3 py-2 text-[11px] text-muted-foreground">
                      {prev}
                    </div>
                  ) : null;
                })()}
                {isCommercialSender && templateDecision.kind === "blocked" && (
                  <p className="text-[10px] text-muted-foreground">
                    No se puede enviar otra plantilla todavía: deben pasar 24 horas desde el último envío sin respuesta del cliente.
                  </p>
                )}
                {isCommercialSender && templateDecision.kind === "text" && (
                  <p className="text-[10px] text-muted-foreground">
                    La conversación está abierta: esta plantilla se enviará como texto para no consumir una plantilla de WhatsApp.
                  </p>
                )}
                {withinWindow && (
                  <p className="text-[10px] text-muted-foreground">
                    Ventana de 24 h abierta: puedes escribir texto libre. Fuera de ese plazo el chat se bloquea y solo se permiten plantillas.
                  </p>
                )}
                {actionErr && <p className="text-xs text-destructive">{actionErr}</p>}
              </div>
            </>
          )}
        </section>
      </div>
      )}

      {/* Handoff-to-Operaciones modal (with context note) */}
      {showHandoff && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={() => setShowHandoff(false)}>
          <div className="w-full max-w-md rounded-2xl bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="mb-1 text-lg font-bold text-foreground">Asignar a Operaciones</h2>
            <p className="mb-4 text-sm text-muted-foreground">
              El chat pasa a <strong>{selected.name || selected.phone}</strong> al grupo de Operaciones y deja de notificar a Comercial.
              Añade una nota de traspaso para que Operaciones tenga contexto.
            </p>
            <textarea
              value={handoffNote}
              onChange={(e) => setHandoffNote(e.target.value)}
              rows={4}
              placeholder="Ej.: Cliente con 4 deudas (~18.000€), quiere reunificar. Ya enviada documentación. Llamar mañana por la mañana."
              className={`${inputClass} mb-4`}
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowHandoff(false)}
                className="rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary">
                Cancelar
              </button>
              <button onClick={() => assignToOperaciones(handoffNote.trim())} className={btnPrimary}>
                Derivar a Operaciones →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* #1 New chat modal */}
      {showNewChat && (
        <NewChatModal
          approvedTemplates={approvedTemplates}
          initialPhone={newChatPhone}
          onClose={() => { setShowNewChat(false); setNewChatPhone(""); }}
          onCreate={startNewChat}
        />
      )}
    </main>
  );
};

// ── Templates management (admin) ─────────────────────────────────────────
const STATUS_STYLE: Record<string, string> = {
  APPROVED: "bg-[#00875a]/10 text-[#00875a]",
  PENDING: "bg-amber-500/10 text-amber-600",
  REJECTED: "bg-destructive/10 text-destructive",
  PAUSED: "bg-muted text-muted-foreground",
  DISABLED: "bg-destructive/10 text-destructive",
};

const TemplatesPanel = ({
  templates,
  syncing,
  syncMsg,
  onSync,
}: {
  templates: Template[];
  syncing: boolean;
  syncMsg: { type: "ok" | "err"; text: string } | null;
  onSync: () => void;
}) => {
  const approved = templates.filter((t) => (t.status || "APPROVED") === "APPROVED").length;
  return (
    <div className="min-h-0 flex-1 overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-4xl">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold text-foreground">Plantillas de mensaje</h1>
            <p className="text-sm text-muted-foreground">
              {templates.length} plantilla(s) · {approved} aprobada(s). Las aprobadas se pueden enviar desde el chat.
            </p>
          </div>
          <button onClick={onSync} disabled={syncing} className={btnPrimary}>
            {syncing ? "Sincronizando…" : "↻ Sincronizar desde Facebook/Meta"}
          </button>
        </div>

        {syncMsg && (
          <p className={`mb-4 text-sm ${syncMsg.type === "ok" ? "text-[#00875a]" : "text-destructive"}`}>
            {syncMsg.text}
          </p>
        )}

        {templates.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center">
            <p className="text-sm text-muted-foreground">
              No hay plantillas todavía. Pulsa <strong>Sincronizar desde Facebook/Meta</strong> para traer las plantillas
              aprobadas de tu cuenta de WhatsApp Business.
            </p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {templates.map((t) => {
              const status = (t.status || "APPROVED").toUpperCase();
              return (
                <div key={t.id} className="rounded-2xl border border-border bg-card p-4 shadow-sm">
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-foreground">{t.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {(t.language || "es").toUpperCase()}
                        {t.category ? ` · ${t.category}` : ""}
                        {t.variableCount ? ` · ${t.variableCount} variable(s)` : ""}
                      </div>
                    </div>
                    <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold ${STATUS_STYLE[status] || "bg-muted text-muted-foreground"}`}>
                      {status}
                    </span>
                  </div>
                  {t.preview ? (
                    <pre className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded-lg bg-secondary/60 p-3 text-xs text-foreground">
                      {t.preview}
                    </pre>
                  ) : (
                    <p className="text-xs italic text-muted-foreground">Sin vista previa de texto.</p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-6 text-xs text-muted-foreground">
          Las plantillas se crean y aprueban en Meta (WhatsApp Manager). Aquí solo se sincronizan: el botón trae el estado
          actual desde Facebook. Una vez <strong>APPROVED</strong>, aparecen en el selector de plantillas del chat.
        </p>
      </div>
    </div>
  );
};

// ── Predefined texts (snippets) management — admin only ──────────────────
const SnippetsPanel = ({
  snippets,
  onAdd,
  onDelete,
  onUpdate,
}: {
  snippets: Snippet[];
  onAdd: (label: string, text: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onUpdate: (id: string, label: string, text: string) => Promise<void>;
}) => {
  const [label, setLabel] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [editId, setEditId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [editText, setEditText] = useState("");

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    setErr("");
    try {
      await onAdd(label, text);
      setLabel("");
      setText("");
    } catch {
      setErr("No se pudo guardar el texto.");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (s: Snippet) => {
    setEditId(s.id);
    setEditLabel(s.label || "");
    setEditText(s.text || "");
  };
  const saveEdit = async () => {
    if (!editId || !editText.trim()) return;
    await onUpdate(editId, editLabel, editText);
    setEditId(null);
  };

  return (
    <div className="min-h-0 flex-1 overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-3xl">
        <h1 className="text-lg font-bold text-foreground">Textos predeterminados</h1>
        <p className="mb-4 text-sm text-muted-foreground">
          Mensajes reutilizables. Solo los administradores pueden crearlos, editarlos o borrarlos; aparecen en el
          desplegable “Texto predeterminado…” del chat para todo el equipo.
        </p>

        {/* Create */}
        <div className="mb-6 rounded-2xl border border-border bg-card p-4">
          <label className="mb-1.5 block text-sm font-medium text-foreground">Nombre (opcional)</label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Ej.: Saludo inicial"
            className={`${inputClass} mb-3 py-2`}
          />
          <label className="mb-1.5 block text-sm font-medium text-foreground">Texto</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            placeholder="Escribe el mensaje predeterminado…"
            className={`${inputClass} mb-3`}
          />
          <div className="flex items-center justify-between">
            {err ? <span className="text-xs text-destructive">{err}</span> : <span />}
            <button onClick={submit} disabled={busy || !text.trim()} className={btnPrimary}>
              {busy ? "Guardando…" : "Añadir texto"}
            </button>
          </div>
        </div>

        {/* List */}
        {snippets.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center">
            <p className="text-sm text-muted-foreground">Aún no hay textos. Crea el primero arriba.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {snippets.map((s) =>
              editId === s.id ? (
                <div key={s.id} className="rounded-xl border border-primary bg-card p-3">
                  <input
                    type="text"
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    placeholder="Nombre (opcional)"
                    className={`${inputClass} mb-2 py-2`}
                  />
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    rows={3}
                    className={`${inputClass} mb-2`}
                  />
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setEditId(null)}
                      className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-secondary">
                      Cancelar
                    </button>
                    <button onClick={saveEdit} disabled={!editText.trim()} className={btnPrimary}>Guardar</button>
                  </div>
                </div>
              ) : (
                <div key={s.id} className="flex items-start justify-between gap-3 rounded-xl border border-border bg-card p-3">
                  <div className="min-w-0">
                    {s.label && <div className="text-sm font-semibold text-foreground">{s.label}</div>}
                    <div className="whitespace-pre-wrap text-sm text-muted-foreground">{s.text}</div>
                    {s.by && <div className="mt-1 text-[10px] text-muted-foreground">por {shortName(s.by)}</div>}
                  </div>
                  <div className="flex shrink-0 gap-1.5">
                    <button
                      onClick={() => startEdit(s)}
                      title="Editar"
                      className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-secondary"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => { if (window.confirm("¿Eliminar este texto predeterminado?")) onDelete(s.id); }}
                      title="Eliminar"
                      className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── New chat modal (#1) ──────────────────────────────────────────────────
const NewChatModal = ({
  approvedTemplates,
  initialPhone,
  onClose,
  onCreate,
}: {
  approvedTemplates: Template[];
  initialPhone?: string;
  onClose: () => void;
  onCreate: (phone: string, name: string, template: string) => Promise<void>;
}) => {
  const [phone, setPhone] = useState(initialPhone || "");
  const [name, setName] = useState("");
  const [tpl, setTpl] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!phone.trim()) { setErr("Introduce un número."); return; }
    setBusy(true);
    setErr("");
    try {
      await onCreate(phone, name, tpl);
    } catch (e: any) {
      setErr(e?.message || "No se pudo crear el chat.");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="mb-1 text-lg font-bold text-foreground">Nuevo chat</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Inicia una conversación con un número. Como es un contacto nuevo (fuera de las 24 h), debes enviar una
          plantilla aprobada por Meta.
        </p>
        <label className="mb-1.5 block text-sm font-medium text-foreground">Número de teléfono</label>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Ej.: 608546327 o +34 608 546 327"
          className={`${inputClass} mb-3 py-2`}
        />
        <label className="mb-1.5 block text-sm font-medium text-foreground">Nombre (opcional)</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nombre del cliente"
          className={`${inputClass} mb-3 py-2`}
        />
        <label className="mb-1.5 block text-sm font-medium text-foreground">Plantilla a enviar</label>
        <select value={tpl} onChange={(e) => setTpl(e.target.value)} className={`${inputClass} mb-4 py-2`}>
          <option value="">Sin plantilla (solo abrir chat)</option>
          {approvedTemplates.map((t) => (
            <option key={t.id} value={t.name}>{t.name}{t.language ? ` (${t.language})` : ""}</option>
          ))}
        </select>
        {err && <p className="mb-3 text-xs text-destructive">{err}</p>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose}
            className="rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary">
            Cancelar
          </button>
          <button onClick={submit} disabled={busy || !phone.trim()} className={btnPrimary}>
            {busy ? "Creando…" : "Crear chat"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppApp;
